import SumoDidiMedia from "../components/SumoDidiMedia";
import { withSumoDidiUpdates } from "../data/sumoDidiMedia";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { siteConfig, films, featureFilm as originalFeatureFilm, heroImage as heroImg, showreels } from "../data/mock";
import { ArrowRight, ExternalLink } from "lucide-react";

const heroImage = heroImg;

const HomePage = () => {
  const featureFilm = withSumoDidiUpdates(originalFeatureFilm);
  const [loaded, setLoaded] = useState(false);

  // Showcase portfolio order (18 commercials)
  const showcaseVimeoIds = [
    '1006490849', // Foxtale 'Be Unstoppable'
    '675932484',  // Kinley
    '542683872',  // Jeevansathi 'Umpire'
    '124905225',  // Maaza Jango
    '636086222',  // Amrutanjan 'Doggy's Day Out'
    '351737288',  // Layer'r Wottagirl Perfume
    '217783762',  // Real Activ
    '174019709',  // Cleartrip Local
    '138602395',  // Carwale 'Unnis Bees'
    '97720127',   // Maaza Time Machine
    '60225182',   // Voltas Murthy
    '60165269',   // Maaza Asur
    '60165101',   // Polycrol
    '100485176',  // AskMe 'Deewar'
    '124184638',  // Domino's AHA Value
    '187648550',  // Nilkamal Mattress 'The Fight'
    '60148540',   // Teacity
    '57849759',   // The Slim Film
  ];
  const selectedCommercials = showcaseVimeoIds
    .map(vid => films.find(f => f.embed && f.embed.includes(vid)))
    .filter(Boolean);

  useEffect(() => {
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setLoaded(true);
    const timer = setTimeout(() => setLoaded(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <motion.div
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 8, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: "brightness(0.55)",
            }}
          />
        </motion.div>

        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 30 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="font-serif text-white text-center text-2xl sm:text-3xl md:text-[40px] lg:text-[48px] leading-tight md:leading-[1.3] max-w-[800px] tracking-[-0.01em]"
          >
            {siteConfig.manifesto}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: loaded ? 0.7 : 0 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="text-white text-[12px] tracking-[0.2em] uppercase mt-10 hidden"
          >
            {siteConfig.director} — {siteConfig.name}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-[1px] h-10 bg-white/40 animate-pulse" />
        </motion.div>
      </section>

      {/* Feature Film — Sumo Didi */}
      <section className="bg-white pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-12"
          >
            Feature Film
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-5">
                <img
                  src="/sumo-didi-official-poster.webp"
                  alt="Sumo Didi official poster"
                  width="1600"
                  height="2000"
                  loading="lazy"
                  className="block w-full max-w-[420px] h-auto mx-auto"
                />
              </div>
              <div className="lg:col-span-7 lg:py-4">
                <h2 className="font-serif text-[28px] md:text-[36px] text-[#1a1a1a] tracking-[-0.02em]">
                  {featureFilm.title}
                </h2>
                <p className="text-[12px] text-[#999] mt-2 tracking-[0.06em]">
                  Hindi — {featureFilm.year}
                </p>
                <p className="text-[15px] text-[#4a4a4a] mt-6 leading-[1.8] text-justify">
                  {featureFilm.logline}
                </p>
                <div className="mt-8 space-y-6">
                  {featureFilm.festivals.map((f) => (
                    <div key={f.name} className="border-l-2 border-[#d6d0c6] pl-4 py-1">
                      <p className="text-[13px] md:text-[14px] font-medium text-[#1a1a1a] tracking-[0.02em] leading-[1.6]">
                        {f.name}
                      </p>
                      <p className="text-[12px] md:text-[13px] text-[#666] mt-2 leading-[1.7]">
                        {f.note}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col items-start gap-5">
                  <Link 
                    to={`/work/${featureFilm.id}`} 
                    className="group/link inline-flex min-h-[44px] items-center gap-3 bg-[#1a1a1a] px-6 py-3 text-[12px] font-medium tracking-[0.12em] uppercase text-white hover:bg-[#3a3a3a] transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1a1a1a]"
                  >
                    View Project
                    <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="mt-16 md:mt-20 max-w-[960px] mx-auto">
              <SumoDidiMedia only="trailer" showLinks />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advertising Showreels */}
      <section className="bg-white pt-16 md:pt-24 pb-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-12"
          >
            Advertising Showreels
          </motion.p>
          <div className="flex flex-wrap gap-x-14 gap-y-6">
            {showreels.map((reel, i) => (
              <motion.a
                key={reel.label}
                href={reel.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group inline-flex items-center gap-3 font-serif text-[26px] md:text-[34px] tracking-[-0.01em] text-[#1a1a1a] transition-colors duration-500"
              >
                <span className="relative inline-block">
                  {reel.label}
                  <span className="pointer-events-none absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-[#1a1a1a] transition-transform duration-500 ease-out group-hover:scale-x-100" />
                </span>
                <ExternalLink size={18} className="opacity-30 transition-all duration-500 group-hover:opacity-80 group-hover:translate-x-0.5" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Commercials — Vimeo order */}
      <section className="bg-white py-16 md:py-24 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-16"
          >
            Selected Commercials
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {selectedCommercials.map((film, i) => (
              <motion.div
                key={film.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <Link to={`/work/${film.id}`} className="group block">
                  <div className="aspect-[16/9] overflow-hidden bg-[#f5f4f2] relative">
                    <img
                      src={film.thumbnail}
                      alt={film.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                    />
                    {film.embed && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-black/10" />
                        <div className="relative flex items-center justify-center w-14 h-14 rounded-full border border-white/80 bg-black/25 backdrop-blur-[2px] scale-90 group-hover:scale-100 transition-transform duration-500 ease-out">
                          <svg width="13" height="13" viewBox="0 0 12 12" fill="white" className="ml-[2px]" aria-hidden="true">
                            <path d="M2 1.5v9l8-4.5z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-5">
                    <h3 className="font-serif text-[18px] text-[#1a1a1a] tracking-[-0.01em]">
                      {film.title}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 flex justify-center"
          >
            <Link
              to="/work"
              className="group inline-flex items-center gap-3 text-[12px] tracking-[0.15em] uppercase text-[#1a1a1a] opacity-60 hover:opacity-100 transition-opacity duration-500"
            >
              View all work
              <ArrowRight
                size={14}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Thematic Statement */}
      <section className="bg-[#fafaf8] py-24 md:py-32">
        <div className="max-w-[700px] mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-serif text-[18px] md:text-[22px] leading-[1.8] text-[#3a3a3a] tracking-[0.01em] text-justify"
          >
            {siteConfig.thematicStatement}
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;